/**
 * mobile-conversation-row.tsx — Hàng hội thoại trên ĐIỆN THOẠI, bám mẫu Messenger của Lark Mobile:
 * avatar tròn lớn với huy hiệu SỐ chưa đọc đỏ ở góc · tên đậm + nhãn nhỏ (kênh / "AI") + giờ bên phải · xem trước tin cuối
 * (+ ghim) bên dưới · không kẻ ngang. Nhấn giữ = chọn nhiều (như bản máy tính).
 * VUỐT TRÁI để lộ ba nút: Ghim / Bỏ ghim · Chưa đọc · Thêm (chuyển Nội bộ ↔ Ưu tiên, Xoá) — cử chỉ quen thuộc của Lark.
 */
import { useState } from 'react'
import { MailOpen, MoreHorizontal, Pin, PinOff, Star, Trash2, Users } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { PlatformBadge } from '@/components/shared/platform-mark'
import { accountColor, channelTitle } from './channel-label'
import {
  useDeleteConversation, useMarkUnread, useSetConversationTab, useTogglePin,
  type ConversationListItem, type ConvTab,
} from '@/hooks/use-conversations'
import { useSwipeReveal } from '@/hooks/use-swipe-reveal'
import { apiError } from '@/lib/api-client'
import { useLongPress } from './conversation-bulk'
import { formatRelativeTime, messagePreview } from './lib'
import { AiAgentChip } from './ai-agent-chip'
import { platformLabelOf } from '@/lib/platform-labels'
import { cn, initials } from '@/lib/utils'

export const shortUnread = (n: number) => (n > 99 ? '99+' : String(n))

/** Tên kênh RÚT GỌN cho nhãn cạnh tên (nhãn dài làm mất chỗ của tên khách): "Facebook Messenger" → "Messenger"… */
const SHORT_CHANNEL: Record<number, string> = {
  2: 'Zalo', 10: 'Messenger', 12: 'Telegram', 13: 'Telegram', 30: 'Pancake', 31: 'Pancake', 32: 'Pancake', 39: 'Pancake',
}
export const shortChannelLabel = (platform: number) => SHORT_CHANNEL[platform] ?? platformLabelOf(platform)

/**
 * Nhãn nhỏ cạnh tên. Doanh nghiệp có nhiều tài khoản cùng loại (nhiều OA / Fanpage…) → TÊN tài khoản (channel-label.ts) — phải
 * biết khách nhắn vào kênh nào; không thì AI đang tự trả lời → "AI" (vàng, như nhãn BOT của Lark); không nữa thì loại kênh (xanh).
 */
export function rowChip(
  conv: Pick<ConversationListItem, 'aiMode' | 'channelAccount'>,
  accountName?: string | null,
): { text: string; tone: 'ai' | 'channel' | 'account' } | null {
  if (accountName) return { text: accountName, tone: 'account' }
  if (conv.aiMode === 'auto') return { text: 'AI', tone: 'ai' }
  const p = conv.channelAccount?.platform
  return p == null ? null : { text: shortChannelLabel(p), tone: 'channel' }
}

/** Bề rộng ba nút vuốt (3 × 76px). */
export const SWIPE_MAX = 228

export function MobileConversationRow({ conv, active, onClick, selectMode, checked, onToggle, onStartSelect, canDelete, showAiAgent, accountName }: {
  conv: ConversationListItem
  active: boolean
  /** Tên tài khoản kênh (OA / Fanpage…) khi có nhiều tài khoản cùng loại — channel-label.ts. */
  accountName?: string | null
  /** Bộ lọc "Nhân viên AI": hiện nhân viên AI + trạng thái. */
  showAiAgent?: boolean
  onClick: () => void
  selectMode: boolean
  checked: boolean
  onToggle: () => void
  onStartSelect: () => void
  canDelete: boolean
}) {
  const { t } = useTranslation('convList')
  const longPress = useLongPress(onStartSelect)
  const swipe = useSwipeReveal({ max: SWIPE_MAX, enabled: !selectMode, id: conv.id })
  const [moreOpen, setMoreOpen] = useState(false)
  const togglePin = useTogglePin()
  const markUnread = useMarkUnread()
  const setTab = useSetConversationTab()
  const del = useDeleteConversation()
  const unread = conv.unreadCount > 0
  const name = conv.displayName || conv.contact?.fullName || t('shared.unnamed')
  const chip = rowChip(conv, accountName)
  const isInternal = conv.tab === 'internal'

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok) } catch (err) { toast.error(apiError(err)) }
    swipe.close()
    setMoreOpen(false)
  }
  const pin = () => run(() => togglePin.mutateAsync({ id: conv.id, pin: !conv.isPinned }), conv.isPinned ? t('mrow.unpinned') : t('mrow.pinnedTop'))
  const markUnreadNow = () => run(() => markUnread.mutateAsync(conv.id), t('mrow.markedUnread'))
  const moveTab = () => run(() => setTab.mutateAsync({ id: conv.id, tab: (isInternal ? 'main' : 'internal') as ConvTab }), isInternal ? t('mrow.movedMain') : t('mrow.movedInternal'))
  const remove = () => {
    if (!window.confirm(t('mrow.confirmDelete', { name }))) return
    void run(() => del.mutateAsync(conv.id), t('mrow.deleted'))
  }

  // Nhấn giữ (chọn nhiều) và vuốt dùng chung các sự kiện chạm: nhấn giữ tự huỷ khi ngón tay trôi đi (xem useLongPress).
  const handlers = {
    onPointerDown: (e: React.PointerEvent) => { longPress.handlers.onPointerDown(e); swipe.handlers.onPointerDown(e) },
    onPointerMove: (e: React.PointerEvent) => { longPress.handlers.onPointerMove(e); swipe.handlers.onPointerMove(e) },
    onPointerUp: (e: React.PointerEvent) => { longPress.handlers.onPointerUp(); swipe.handlers.onPointerUp(e) },
    onPointerLeave: longPress.handlers.onPointerLeave,
    onPointerCancel: () => { longPress.handlers.onPointerCancel(); swipe.handlers.onPointerCancel() },
    onContextMenu: longPress.handlers.onContextMenu,
  }

  return (
    <li className="relative overflow-hidden">
      {/* Nút lộ ra khi vuốt trái — nằm phía sau hàng */}
      {!selectMode && (
        <div className="absolute inset-y-0 right-0 flex" style={{ width: SWIPE_MAX }} aria-hidden={!swipe.open}>
          <SwipeAction label={conv.isPinned ? t('common:actions.unpin') : t('common:actions.pin')} icon={conv.isPinned ? PinOff : Pin} tone="bg-primary text-primary-foreground" onClick={pin} tabIndex={swipe.open ? 0 : -1} />
          <SwipeAction label={t('shared.unread')} icon={MailOpen} tone="bg-warning text-white" onClick={markUnreadNow} tabIndex={swipe.open ? 0 : -1} />
          <SwipeAction label={t('common:actions.more')} icon={MoreHorizontal} tone="bg-slate-500 text-white" onClick={() => setMoreOpen(true)} tabIndex={swipe.open ? 0 : -1} />
        </div>
      )}
      <div
        className={cn('relative bg-background', !swipe.dragging && 'transition-transform duration-200 ease-out')}
        style={{ transform: `translateX(${swipe.offset}px)`, touchAction: 'pan-y' }}
      >
        <button
          type="button"
          {...handlers}
          onClick={() => {
            if (longPress.wasLongPress()) return
            if (swipe.consumeClick()) return
            if (selectMode) onToggle()
            else onClick()
          }}
          className={cn(
            'flex w-full select-none items-center gap-3.5 px-4 py-3 text-left text-foreground transition-colors active:bg-muted/60 [-webkit-touch-callout:none]',
            active && !selectMode && 'bg-accent',
            selectMode && checked && 'bg-primary/10',
          )}
        >
        {selectMode && (
          <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2', checked ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')} aria-hidden>
            {checked && <svg viewBox="0 0 12 12" className="h-3 w-3"><path d="M2.5 6.2 5 8.6l4.5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          </span>
        )}
        <div className="relative shrink-0">
          <Avatar className="h-[54px] w-[54px]">
            {conv.contact?.avatarUrl && <AvatarImage src={conv.contact.avatarUrl} alt={name} />}
            <AvatarFallback className="text-base">{initials(name)}</AvatarFallback>
          </Avatar>
          {unread && !selectMode && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-none text-destructive-foreground ring-2 ring-background">
              {shortUnread(conv.unreadCount)}
            </span>
          )}
          <PlatformBadge platform={conv.channelAccount?.platform} title={channelTitle(conv.channelAccount)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('min-w-0 truncate text-[17px] leading-tight text-foreground', unread ? 'font-semibold' : 'font-medium')}>{name}</span>
            {chip && (
              <span
                className={cn(
                  'shrink-0 truncate rounded px-1.5 py-[1px] text-[11px] font-medium leading-tight',
                  chip.tone === 'account' ? 'max-w-[7.5rem]' : 'max-w-[5.5rem]',
                  chip.tone === 'ai' ? 'bg-warning/15 text-warning' : chip.tone === 'channel' && 'bg-primary/10 text-primary',
                )}
                // Nhãn tài khoản tô màu riêng theo từng tài khoản (ổn định, xem channel-label.ts#accountColor) — nhận ra
                // ngay hội thoại thuộc tài khoản nào, giống thẻ màu của Lark; các loại nhãn khác (AI/kênh) giữ màu cố định.
                style={chip.tone === 'account' && conv.channelAccount ? {
                  backgroundColor: `${accountColor(conv.channelAccount.id)}22`, color: accountColor(conv.channelAccount.id),
                } : undefined}
                title={chip.tone === 'account' ? channelTitle(conv.channelAccount) : undefined}
              >
                {chip.text}
              </span>
            )}
            <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[13px] text-muted-foreground">
              {!conv.isReplied && <span className="h-2 w-2 rounded-full bg-warning" aria-label={t('shared.unreplied')} />}
              {formatRelativeTime(conv.lastMessageAt)}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            {showAiAgent && conv.aiAgent && <AiAgentChip agent={conv.aiAgent} />}
            <span className={cn('min-w-0 flex-1 truncate text-[15px]', unread ? 'text-foreground/80' : 'text-muted-foreground')}>{messagePreview(conv.messages?.[0])}</span>
            {conv.isPinned && <Pin className="h-3.5 w-3.5 shrink-0 fill-destructive text-destructive" aria-label={t('shared.pinned')} />}
          </div>
        </div>
        </button>
      </div>

      {/* "Thêm": các thao tác còn lại — hộp trượt từ đáy (như bảng hành động của Lark) */}
      <BottomSheet open={moreOpen} onOpenChange={setMoreOpen}>
        <BottomSheetContent title={t('mrow.actionsFor', { name })}>
          <div className="space-y-2 px-4 pb-4 pt-3">
            <p className="truncate px-1 text-sm font-medium text-muted-foreground">{name}</p>
            <ul className="divide-y overflow-hidden rounded-xl border bg-card text-[15px]">
              <li>
                <SheetRow icon={isInternal ? Star : Users} label={isInternal ? t('mrow.toMain') : t('mrow.toInternal')} onClick={moveTab} />
              </li>
              {canDelete && (
                <li>
                  <SheetRow icon={Trash2} label={t('mrow.delete')} danger onClick={remove} />
                </li>
              )}
            </ul>
          </div>
        </BottomSheetContent>
      </BottomSheet>
    </li>
  )
}

function SwipeAction({ label, icon: Icon, tone, onClick, tabIndex }: { label: string; icon: React.ComponentType<{ className?: string }>; tone: string; onClick: () => void; tabIndex: number }) {
  return (
    <button type="button" tabIndex={tabIndex} onClick={onClick} className={cn('flex flex-1 flex-col items-center justify-center gap-1 text-[12px] font-medium active:brightness-90', tone)}>
      <Icon className="h-5 w-5" />
      {label}
    </button>
  )
}

function SheetRow({ icon: Icon, label, danger, onClick }: { icon: React.ComponentType<{ className?: string }>; label: string; danger?: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={cn('flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-muted', danger && 'text-destructive')}>
      <Icon className="h-5 w-5" />
      {label}
    </button>
  )
}
